import React from "react";
import { PlayIcon as PlayIconComponent, PlayLoadingIcon as PlayLoadingIconComponent, PlayingIcon as PlayingIconComponent } from "@/components/ui/icon";
import {
  useAudioController,
  AudioPlayState,
} from "@/manager/manager/audio-controller-manager";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/utils/cn";
import { logger } from "@/utils/logger";

interface AudioPlayButtonProps {
  /** 音频URL */
  url: string;
  /** 按钮样式类名 */
  className?: string;
  /** 图标大小类名，默认 "w-5 h-5" */
  iconClassName?: string;
  PlayIcon?: React.JSXElementConstructor<{ className?: string }>;
  LoadingIcon?: React.JSXElementConstructor<{ className?: string }>;
  PlayingIcon?: React.JSXElementConstructor<{ className?: string }>;
  /** 点击事件回调 */
  onClick?: (e: React.MouseEvent) => void;
  /** 是否禁用 */
  disabled?: boolean;
}

export const AudioPlayButton: React.FC<AudioPlayButtonProps> = ({
  url,
  className,
  iconClassName = "w-5 h-5",
  PlayIcon = PlayIconComponent,
  LoadingIcon = PlayLoadingIconComponent,
  PlayingIcon = PlayingIconComponent,
  disabled = false,
  onClick,
}) => {
  const { playState, currentTime, duration, play, pause, stop } =
    useAudioController();
  const { toast } = useToast();
  const isLoading = playState === AudioPlayState.LOADING;
  const isPlaying = playState === AudioPlayState.PLAYING;

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (disabled) {
      return;
    }

    try {
      isPlaying ? pause() : await play(url);
    } catch (error) {
      stop();
      toast.default({
        title: "Play audio failed",
        description: "Play audio failed, please try again.",
      });
      logger.error("Play audio failed", error);
    }

    onClick?.(e);
  };

  const renderIcon = () => {
    if (isLoading) {
      return (
        <LoadingIcon
          className={cn(
            "w-full h-full text-i-normal hover:text-[rgba(40,47,62,1)]",
            disabled && "text-[rgba(102,112,133,1)] hover:text-[rgba(102,112,133,1)]",
            iconClassName
          )}
        />
      );
    }

    if (isPlaying) {
      const progress =
        duration > 0 ? Math.ceil((currentTime / duration) * 100) : 0;
      return (
        <PlayingIcon
          className={cn(
            "w-full h-full text-i-normal hover:text-[rgba(40,47,62,1)]",
            disabled && "text-[rgba(102,112,133,1)] hover:text-[rgba(102,112,133,1)]",
            iconClassName
          )}
          progress={progress}
        />
      );
    }

    return (
      <PlayIcon
        className={cn(
          "w-full h-full text-i-normal hover:text-[rgba(40,47,62,1)]",
          disabled && "text-[rgba(102,112,133,1)] hover:text-[rgba(102,112,133,1)]",
          iconClassName
        )}
      />
    );
  };

  return (
    <button onClick={handleClick} disabled={disabled} className={className}>
      {renderIcon()}
    </button>
  );
};
